import { CaretDownFilled, CaretUpFilled, EmptyIcon } from '@/assets/icons';
import { ImagesMobile } from '@/assets/images';
import { useEditableDetail } from '@/hooks';
import { isMobile } from '@/utils/mobile.util';
import {
	ForwardedRef,
	forwardRef,
	memo,
	useCallback,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Select as SelectAntd, SelectProps } from 'antd';
import { DefaultOptionType, RefSelectProps } from 'antd/es/select';
import {
	cn,
	getTranslatedMessage,
	insertObjectIfElse,
	insertObjectIfV2,
	mergeRefs,
} from 'utils';

import './style.scss';
import { Namespace } from 'i18next';
import { isNotNilOrEmpty, isNull } from 'ramda-adjunct';

export interface ISelect extends SelectProps {
	name?: string;
	label?: string;
	containerClassName?: string;
	required?: boolean;
	horizontal?: boolean;
	labelClassName?: string;
	textEditClassName?: string;
	hasServerError?: boolean;
	errorCode?: string | undefined;
	touched?: boolean;
	isDetail?: boolean;
	searchStartsWith?: boolean;
	translationLimit?: boolean | number;
	ns?: Namespace;
}
const Select = (
	{
		label,
		containerClassName = '',
		required = false,
		horizontal = true,
		labelClassName = '',
		textEditClassName = '',
		className,
		touched,
		errorCode,
		hasServerError,
		isDetail = false,
		searchStartsWith = false,
		translationLimit = false,
		ns = 'common',
		...rest
	}: ISelect,
	ref: ForwardedRef<RefSelectProps>
) => {
	const { t } = useTranslation(ns);

	const innerSelectRef = useRef<RefSelectProps>(null);

	const mergedRef = mergeRefs(innerSelectRef, ref);

	const { isUpdate, editable, handleLabelClick } = useEditableDetail({
		fieldName: rest.name as string,
	});

	const [openDropdown, setOpenDropdown] = useState(false);

	const onDropdownVisibleChange: SelectProps['onDropdownVisibleChange'] = (
		open
	) => {
		setOpenDropdown(open);
	};

	const newProperties = useMemo(() => {
		const isOnlySelectOption =
			rest.options?.length === 1 && rest.options[0].label === 'select';
		const isEmptyValue =
			typeof rest.value === 'object'
				? rest.value?.value === ''
				: rest.value === '';

		return isOnlySelectOption && isEmptyValue
			? { options: [], placeholder: t('select'), value: null }
			: {};
	}, [rest.options, rest.value, t]);

	const _onChange = useCallback(
		(value: any, option: DefaultOptionType | DefaultOptionType[]) => {
			rest.onChange?.(value, option);
			innerSelectRef?.current?.blur();
		},
		[rest.onChange]
	);

	const _options = useMemo(() => {
		let newOptions = [...(rest.options || [])];
		if (newOptions.length > 0) {
			newOptions = newOptions.map((option, index) =>
				(translationLimit === true && index > 0) ||
				(typeof translationLimit === 'number' && index >= translationLimit)
					? option
					: { ...option, label: getTranslatedMessage(t, option.label, { ns }) }
			);
		}
		return newOptions;
	}, [rest.options, translationLimit, t, ns]);

	const _value = useMemo(() => {
		const val = rest.value;
		if (isNull(val)) return null;

		// Handle multiple mode - return array directly
		if (rest.mode === 'multiple' || rest.mode === 'tags') {
			if (Array.isArray(val)) {
				return val;
			}
			return [];
		}

		const found = _options?.find((opt) =>
			typeof val === 'object'
				? String(opt.value) === String(val?.value)
				: String(opt.value) === String(val)
		);

		return typeof val === 'object'
			? {
					...found,
					label: getTranslatedMessage(t, found?.label, { ns }),
				}
			: found?.value;
	}, [rest.value, rest.mode, _options, t, ns]);

	const textLabel = useMemo(() => {
		// Handle array value (multiple mode)
		if (Array.isArray(_value)) {
			if (_value.length === 0) return '-';
			// For multiple mode, show count or first item label
			const firstValue = _value[0];
			const firstOption = _options?.find(
				(e: DefaultOptionType) => String(e.value) === String(firstValue)
			);
			return firstOption
				? _value.length > 1
					? `${firstOption.label} (+${_value.length - 1})`
					: firstOption.label
				: '-';
		}

		if (typeof _value !== 'object') {
			return (
				(_value &&
					_options?.find(
						(e: DefaultOptionType) => String(e.value) === String(_value)
					)?.label) ||
				'-'
			);
		}

		return required
			? _value?.label || '-'
			: isNotNilOrEmpty(_value?.value)
				? _value?.label
				: '-';
	}, [required, _value, _options]);

	return (
		<div
			className={cn(
				`flex gap-2 flex-1 w-full`,
				horizontal ? 'items-center' : 'flex-col',
				containerClassName
			)}
		>
			{label && (
				<label
					className={cn(
						'text-[13px] font-medium text-font2',
						horizontal && 'min-w-[110px]',
						labelClassName
					)}
				>
					{label} {required && <span className="text-red6">*</span>}
				</label>
			)}

			{isDetail && !editable ? (
				<div className="flex w-full">
					<div
						className={cn(
							'break-all box-border w-full text-[13px] xl:h-[30px] text-font2 py-[5px] xl:px-[11px] text-left overflow-hidden xl:hover:border xl:hover:border-gray1 cursor-text truncate whitespace-pre',
							rest.disabled &&
								'cursor-not-allowed hover:border-none hover:bg-porcelain',
							textEditClassName
						)}
						onClick={!rest.disabled ? handleLabelClick : undefined}
					>
						{textLabel || '-'}
					</div>
				</div>
			) : (
				<div className={cn('flex-1 w-full')}>
					<SelectAntd
						size="large"
						suffixIcon={
							isMobile ? (
								<img
									src={ImagesMobile.iconAccordion}
									className={openDropdown ? '' : 'rotate-180'}
								/>
							) : openDropdown ? (
								<CaretUpFilled />
							) : (
								<CaretDownFilled />
							)
						}
						style={{ width: '100%' }}
						dropdownAlign={{ offset: [0, 5] }}
						{...insertObjectIfElse(
							searchStartsWith,
							{
								filterOption: (input: string, option?: DefaultOptionType) =>
									(option?.label?.toString() ?? '')
										.toLowerCase()
										.startsWith(input.toLowerCase()),
							},
							{
								filterOption: (input: string, option?: DefaultOptionType) =>
									(option?.label?.toString() ?? '')
										.toLowerCase()
										.includes(input.toLowerCase()),
							}
						)}
						{...rest}
						options={_options}
						value={_value}
						onChange={_onChange}
						className={cn(
							'[&.ant-select-open>div.ant-select-selector]:!border-blue2 [&>div.ant-select-selector]:!px-3 !text-left xl:[&>div.ant-select-selector]:!rounded-none',
							rest.removeIcon &&
								'[&>div.ant-select-selector>.ant-select-selection-item]:!pe-0',
							className,
							isMobile && '[&>.ant-select-selector]:!text-font2'
						)}
						{...insertObjectIfV2(isNotNilOrEmpty(newProperties), newProperties)}
						optionFilterProp="label"
						status={
							(touched && errorCode) || hasServerError ? 'error' : undefined
						}
						notFoundContent={
							<div role="listbox" className="ant-select-item-empty">
								<div className="flex flex-col justify-center items-center">
									<EmptyIcon />
									<p className="mt-2 text-font8">{t('no-information')}</p>
								</div>
							</div>
						}
						onDropdownVisibleChange={onDropdownVisibleChange}
						defaultOpen={isUpdate && editable && isDetail}
						ref={mergedRef}
						onBlur={() => {
							isMobile && window.scrollTo(0, 0);
							rest.onBlur;
						}}
					/>
				</div>
			)}
		</div>
	);
};

export default memo(forwardRef(Select));
